import isEqual from "fast-deep-equal";
import type { JSONContent } from "@tiptap/core";
import type { ScratchpadDocument } from "./document";

export type ScratchpadBlock = NonNullable<ScratchpadDocument["content"]["content"]>[number];

export type ScratchpadConflict =
  | {
      type: "block";
      id: string;
      fields: ScratchpadField[];
      mine: ScratchpadBlock | null;
      theirs: ScratchpadBlock | null;
    }
  | { type: "summary"; mine: string; theirs: string }
  | { type: "clear"; mine: null; theirs: ScratchpadDocument };

export type ScratchpadChoice = "mine" | "theirs";

export function scratchpadConflictKey(conflict: ScratchpadConflict) {
  return conflict.type === "block" ? conflict.id : conflict.type;
}

function text(node: JSONContent): string {
  if (node.type === "hardBreak") return "\n";
  return node.text ?? (node.content ?? []).map(text).join("");
}

export function describeScratchpadConflict(conflict: ScratchpadConflict, choice: ScratchpadChoice): string[] {
  if (conflict.type === "summary") return [conflict[choice] !== "" ? conflict[choice] : "(empty)"];
  if (conflict.type === "clear") {
    if (choice === "mine") return ["Clear everything"];
    const count = conflict.theirs.content.content.length;
    const summary = conflict.theirs.caseSummary !== "" ? " and the summary" : "";
    return [`Keep ${count} ${count === 1 ? "block" : "blocks"}${summary}`];
  }

  const block = conflict[choice];
  if (block == null) return ["Removed"];

  const fields: ScratchpadField[] = conflict.fields.length > 0 ? conflict.fields : ["label", "content"];
  return fields
    .filter((field) => field !== "label" || block.type === "dataPoint")
    .map((field) => {
      if (field === "label") {
        const label = String(block.attrs?.label ?? "");
        return `Label: ${label !== "" ? label : "(empty)"}`;
      }

      if (field === "content") {
        const content = text(block);
        return `${block.type === "dataPoint" ? "Note" : "Text"}: ${content !== "" ? content : "(empty)"}`;
      }

      const { type, marks, attrs } = BLOCK_FIELDS.formatting(block) as JSONContent;
      const details = [
        ...(marks ?? []).map((mark) => mark.type),
        ...Object.entries(attrs ?? {})
          .filter(([, value]) => value != null)
          .map(([name, value]) => `${name} ${String(value)}`),
      ];
      return `Formatting: ${type}${details.length > 0 ? ` (${details.join(", ")})` : ""}`;
    });
}

function sameBlock(left: ScratchpadBlock | undefined, right: ScratchpadBlock | undefined) {
  if (left == null || right == null) return left === right;
  // updatedAt is merge metadata, not a content change; ignore it when detecting edits.
  const { updatedAt: _leftUpdated, ...leftAttrs } = left.attrs ?? {};
  const { updatedAt: _rightUpdated, ...rightAttrs } = right.attrs ?? {};
  return isEqual({ ...left, attrs: leftAttrs }, { ...right, attrs: rightAttrs });
}

function blockId(block: ScratchpadBlock): string | undefined {
  return typeof block.attrs?.id === "string" ? block.attrs.id : undefined;
}

function blockTime(block: ScratchpadBlock) {
  const timestamp: unknown = block.attrs?.updatedAt ?? block.attrs?.createdAt;
  return typeof timestamp === "string" ? Date.parse(timestamp) : 0;
}

// Merge non-conflicting fields independently; when both sides changed one field differently, record it
// and build both alternatives by varying only those fields. Keep identity/timestamps from the newer block.
export const BLOCK_FIELDS = {
  label: (block: ScratchpadBlock): unknown => block.attrs?.label,
  content: (block: ScratchpadBlock): unknown => block.content,
  formatting: ({ content: _content, attrs, ...node }: ScratchpadBlock): unknown => {
    const {
      id: _id,
      createdAt: _createdAt,
      updatedAt: _updatedAt,
      source: _source,
      label: _label,
      ...rest
    } = attrs ?? {};
    return { ...node, attrs: rest };
  },
};

export type ScratchpadField = keyof typeof BLOCK_FIELDS;

// Takes each field from the side that changed it; fields both changed differently are the conflict's.
function mergeBlock(before: ScratchpadBlock | undefined, ours: ScratchpadBlock, theirs: ScratchpadBlock) {
  const fields: ScratchpadField[] = [];
  const changedBy = {} as Record<ScratchpadField, ScratchpadBlock>;

  for (const field of Object.keys(BLOCK_FIELDS) as ScratchpadField[]) {
    const [original, mine, remote] = [before, ours, theirs].map((block) =>
      block == null ? undefined : BLOCK_FIELDS[field](block),
    );
    // Prefer our value only when the remote side left this field unchanged; otherwise keep remote.
    changedBy[field] = isEqual(remote, original) ? ours : theirs;
    if (changedBy[field] === theirs && !isEqual(mine, original) && !isEqual(mine, remote)) fields.push(field);
  }

  const newer = blockTime(ours) > blockTime(theirs) ? ours : theirs;
  const metadata = Object.fromEntries(
    Object.entries(newer.attrs ?? {}).filter(([key]) => ["id", "createdAt", "updatedAt"].includes(key)),
  );
  function build(choice: ScratchpadChoice): ScratchpadBlock {
    const from = (field: ScratchpadField) => (choice === "mine" && fields.includes(field) ? ours : changedBy[field]);
    const { content: _content, attrs, ...node } = from("formatting");
    const label: unknown = from("label").attrs?.label;
    const content = from("content").content;
    return {
      ...node,
      attrs: { ...attrs, ...metadata, ...(label !== undefined && { label }) },
      ...(content !== undefined && { content }),
    };
  }

  return { fields, mine: build("mine"), theirs: build("theirs") };
}

function blocksById(blocks: ScratchpadBlock[]) {
  const result = new Map<string, ScratchpadBlock>();
  for (const block of blocks) {
    const id = blockId(block);
    // Preserve the existing last-occurrence lookup when an input contains duplicate IDs.
    if (id != null) result.set(id, block);
  }
  return result;
}

export function rebaseScratchpad(
  base: ScratchpadDocument | null,
  mine: ScratchpadDocument | null,
  theirs: ScratchpadDocument | null,
  choose: (conflict: ScratchpadConflict) => ScratchpadChoice | undefined = () => undefined,
): { document: ScratchpadDocument | null; conflicts: ScratchpadConflict[]; resolved: boolean } {
  const conflicts: ScratchpadConflict[] = [];
  let resolved = true;

  function pick<T>(conflict: ScratchpadConflict, ours: T, remote: T): T {
    conflicts.push(conflict);
    const choice = choose(conflict);
    // Continue with remote as a provisional value; the final result is withheld if any choice is missing.
    if (choice == null) resolved = false;
    return choice === "mine" ? ours : remote;
  }

  if (mine == null) {
    // A local clear wins over an untouched base; clearing a concurrently changed document needs a choice.
    let document = base == null ? theirs : null;
    if (base != null && theirs != null && !isEqual(base, theirs))
      document = pick({ type: "clear", mine: null, theirs }, null, theirs);

    return { document: resolved ? document : null, conflicts, resolved };
  }

  const ours = mine.content.content ?? [];
  const original = blocksById(base?.content.content ?? []);
  const local = blocksById(ours);
  const remote = blocksById(theirs?.content.content ?? []);

  // Keep output order in a linked list so edits, removals and insertions do not shift an array.
  type ContentNode = {
    block: ScratchpadBlock;
    previous?: ContentNode;
    next?: ContentNode;
    nextWithId?: ContentNode;
  };

  // Seed the linked output with remote order. Keep duplicate-ID nodes chained so removing the first
  // occurrence exposes the next one, matching the former first-match array lookup.
  const positions = new Map<string, ContentNode>();
  let head: ContentNode | undefined;
  const theirContent = theirs?.content.content ?? [];
  for (let index = theirContent.length - 1; index >= 0; index--) {
    const block = theirContent[index];
    const id = blockId(block);
    const node: ContentNode = { block, next: head, nextWithId: id == null ? undefined : positions.get(id) };

    if (head != null) head.previous = node;

    head = node;
    if (id != null) positions.set(id, node);
  }

  // A max segment tree finds the nearest currently present local predecessor in O(log n). Index every
  // occurrence so duplicate IDs remain searchable; queries stop before the new block's first local occurrence.
  const localPositions = new Map<string, number[]>();
  const predecessors = new Array<number>(ours.length * 2).fill(-1);

  for (let index = 0; index < ours.length; index++) {
    const id = blockId(ours[index]);
    if (id == null) continue;

    const indices = localPositions.get(id);
    if (indices == null) localPositions.set(id, [index]);
    else indices.push(index);

    if (positions.has(id)) predecessors[ours.length + index] = index;
  }

  for (let index = ours.length - 1; index > 0; index--) {
    predecessors[index] = Math.max(predecessors[index * 2], predecessors[index * 2 + 1]);
  }

  function setPresent(id: string, present: boolean) {
    for (const index of localPositions.get(id) ?? []) {
      let position = ours.length + index;
      predecessors[position] = present ? index : -1;

      while (position > 1) {
        position = Math.floor(position / 2);
        predecessors[position] = Math.max(predecessors[position * 2], predecessors[position * 2 + 1]);
      }
    }
  }

  function predecessor(id: string) {
    let left = ours.length;
    let right = ours.length + (localPositions.get(id)?.[0] ?? 0);
    let nearest = -1;

    while (left < right) {
      if (left % 2 === 1) nearest = Math.max(nearest, predecessors[left++]);
      if (right % 2 === 1) nearest = Math.max(nearest, predecessors[--right]);
      left = Math.floor(left / 2);
      right = Math.floor(right / 2);
    }

    return nearest === -1 ? undefined : positions.get(blockId(ours[nearest])!);
  }

  // Identical changes need no conflict; an unchanged side yields to the changed side; concurrent
  // deletion/edit or independent field edits require a choice.
  for (const id of new Set([...original.keys(), ...local.keys()])) {
    const before = original.get(id);
    const ourBlock = local.get(id);
    const theirBlock = remote.get(id);
    let block = theirBlock;

    if (sameBlock(ourBlock, theirBlock)) {
      if (ourBlock != null && theirBlock != null) {
        block = blockTime(ourBlock) > blockTime(theirBlock) ? ourBlock : theirBlock;
      }
    } else if (sameBlock(ourBlock, before)) {
      continue;
    } else if (sameBlock(theirBlock, before)) {
      block = ourBlock;
    } else if (ourBlock == null || theirBlock == null) {
      block = pick(
        { type: "block", id, fields: [], mine: ourBlock ?? null, theirs: theirBlock ?? null },
        ourBlock,
        theirBlock,
      );
    } else {
      const merged = mergeBlock(before, ourBlock, theirBlock);
      block = merged.theirs;
      if (merged.fields.length > 0) {
        const conflict = { type: "block", id, fields: merged.fields, mine: ourBlock, theirs: theirBlock } as const;
        block = pick(conflict, merged.mine, merged.theirs);
      }
    }

    const position = positions.get(id);

    if (position != null) {
      if (block == null) {
        // Remove only the first remote occurrence. Keep the ID searchable while another duplicate remains.
        if (position.previous == null) head = position.next;
        else position.previous.next = position.next;

        if (position.next != null) position.next.previous = position.previous;

        if (position.nextWithId != null) positions.set(id, position.nextWithId);
        else {
          positions.delete(id);
          setPresent(id, false);
        }
      } else position.block = block;
    } else if (block != null) {
      // New/restored blocks go after the nearest surviving local predecessor, or at the front if none;
      // inserting relative to remote order leaves unrelated remote blocks in place.
      const previous = predecessor(id);
      const next = previous == null ? head : previous.next;
      const node: ContentNode = { block, previous, next };

      if (previous == null) head = node;
      else previous.next = node;

      if (next != null) next.previous = node;

      positions.set(id, node);
      setPresent(id, true);
    }
  }

  const content: ScratchpadBlock[] = [];
  for (let node = head; node != null; node = node.next) content.push(node.block);

  // Merge the summary as one field: unchanged local content accepts remote, divergent edits conflict.
  const beforeSummary = base?.caseSummary ?? "";
  const remoteSummary = theirs?.caseSummary ?? "";
  let caseSummary = remoteSummary;

  if (mine.caseSummary !== beforeSummary && mine.caseSummary !== remoteSummary) {
    caseSummary = mine.caseSummary;

    if (remoteSummary !== beforeSummary) {
      caseSummary = pick(
        { type: "summary", mine: mine.caseSummary, theirs: remoteSummary },
        mine.caseSummary,
        remoteSummary,
      );
    }
  }

  // A remotely absent, now-empty document collapses to null; unresolved conflicts never expose a partial merge.
  const document: ScratchpadDocument | null =
    theirs == null && content.length === 0 && caseSummary === ""
      ? null
      : { ...mine, caseSummary, content: { ...(theirs ?? mine).content, content } };

  return { document: resolved ? document : null, conflicts, resolved };
}
